/**
 * WhatIfNisDilCalculator.jsx — iter866
 *
 * Interactive popover attached to a BTRE Step 11 meal badge. Lets the
 * patient tweak protein / carbs / fiber / fat and see how NIS %% + DIL g
 * would change relative to the baseline meal. Debounced 250ms; hits the
 * canonical backend endpoint /api/bhi/nis-dil-whatif so both scores stay
 * authoritative (frontend never re-implements the SDK formula).
 *
 * Props:
 *   baseline: { protein_g, carbs_g, fiber_g, fat_g,
 *               nis_pct, dil_g, name_bg }
 *   isDark:   bool
 *   onClose:  () => void
 *   apiBase:  string  (REACT_APP_BACKEND_URL)
 *   token:    string  (JWT for Authorization header)
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { X, RotateCcw, TrendingDown, TrendingUp, Info } from "lucide-react";

const CLAMP = (v, min, max) => Math.min(max, Math.max(min, v));

const SLIDERS = [
  { key: "protein_g", label: "Протеин", unit: "г", min: 0, max: 80, step: 1 },
  { key: "carbs_g",   label: "Въглехидрати", unit: "г", min: 0, max: 120, step: 1 },
  { key: "fiber_g",   label: "Фибри", unit: "г", min: 0, max: 30, step: 0.5 },
  { key: "fat_g",     label: "Мазнини", unit: "г", min: 0, max: 60, step: 1 },
];

// iter868 — Preset swap chips. Each swap describes a common ingredient
// exchange and the macro delta it produces (Δ protein_g, Δ carbs_g,
// Δ fiber_g, Δ fat_g). These are derived from the Bulgarian food-base:
//   · 100 г бял ориз (варен) → 100 г киноа (варена)
//   · 100 г бял хляб → 100 г пълнозърнест хляб
//   · 100 г бял ориз → 100 г карфиоров ориз
//   · 100 г тестени → 100 г спагети от тиквички
//   · 15 г захар → 100 г ягоди
// Each swap is a *relative* change applied on top of the current macros;
// the user can stack multiple.
const PRESET_SWAPS = [
  {
    id:      "rice_to_quinoa",
    label:   "Ориз → киноа",
    tooltip: "100 г бял ориз (варен) → 100 г киноа (варена). Повече протеин + фибри.",
    delta:   { protein_g: +3.6, carbs_g: -6.2, fiber_g: +2.0, fat_g: +1.6 },
  },
  {
    id:      "white_to_wholegrain_bread",
    label:   "Бял → пълнозърнест",
    tooltip: "50 г бял хляб → 50 г пълнозърнест. Повече фибри, малко по-малко въгл.",
    delta:   { protein_g: +2.0, carbs_g: -3.0, fiber_g: +4.5, fat_g: +0.5 },
  },
  {
    id:      "rice_to_cauliflower",
    label:   "Ориз → карфиоров ориз",
    tooltip: "100 г бял ориз → 100 г карфиоров ориз. Драстично -въгл. + фибри.",
    delta:   { protein_g: 0, carbs_g: -22.0, fiber_g: +2.5, fat_g: 0 },
  },
  {
    id:      "pasta_to_zoodles",
    label:   "Тестени → тиквичкови нудли",
    tooltip: "80 г спагети → 200 г тиквички. Ниско-въгл. алтернатива.",
    delta:   { protein_g: -4.0, carbs_g: -50.0, fiber_g: +2.0, fat_g: +0.5 },
  },
  {
    id:      "sugar_to_berries",
    label:   "Захар → плодове",
    tooltip: "15 г захар → 100 г ягоди. Фибри + витамин C.",
    delta:   { protein_g: +0.5, carbs_g: -7.0, fiber_g: +2.0, fat_g: 0 },
  },
  {
    id:      "add_fiber",
    label:   "+ фибри (леща/чиа)",
    tooltip: "Добави 20 г леща или 10 г чиа за +6 г фибри без много въгл.",
    delta:   { protein_g: +5.0, carbs_g: +8.0, fiber_g: +6.0, fat_g: +2.0 },
  },
  {
    id:      "milk_to_almond_milk",
    label:   "Мляко → бадемово",
    tooltip: "200 мл краве мляко → 200 мл бадемово. Драстично -въгл. и -лактоза.",
    delta:   { protein_g: -6.0, carbs_g: -9.0, fiber_g: +0.5, fat_g: -3.0 },
  },
  {
    id:      "red_meat_to_chicken",
    label:   "Червено месо → пилешко",
    tooltip: "130 г телешко/агнешко → 130 г пилешки гърди. Постна протеин.",
    delta:   { protein_g: +5.0, carbs_g: 0, fiber_g: 0, fat_g: -14.0 },
  },
  {
    id:      "seed_oil_to_olive",
    label:   "Слънчогл. олио → зехтин",
    tooltip: "10 г слънчогл. олио → 10 г екстра-върджин зехтин. Полифеноли + oleocanthal.",
    delta:   { protein_g: 0, carbs_g: 0, fiber_g: 0, fat_g: 0 },
  },
  {
    id:      "sugar_yogurt_to_plain",
    label:   "Плодов йогурт → чист",
    tooltip: "150 г плодов йогурт → 150 г кисело мляко натурал. Без добавена захар.",
    delta:   { protein_g: +3.0, carbs_g: -15.0, fiber_g: 0, fat_g: +1.0 },
  },
  {
    id:      "croissant_to_oatmeal",
    label:   "Кроасан → овесена каша",
    tooltip: "60 г кроасан → 50 г овес + мляко. -рафинирани въгл., +β-глюкан.",
    delta:   { protein_g: +6.0, carbs_g: -5.0, fiber_g: +7.0, fat_g: -8.0 },
  },
  {
    id:      "pasta_to_chickpea",
    label:   "Тестени → нахутени",
    tooltip: "80 г спагети → 80 г нахутени спагети. +протеин, +фибри при същите въгл.",
    delta:   { protein_g: +12.0, carbs_g: -3.0, fiber_g: +8.0, fat_g: +2.0 },
  },
];

export const WhatIfNisDilCalculator = ({
  baseline,
  isDark = false,
  onClose,
  apiBase,
  token,
}) => {
  // Guard against missing macros — fall back to zeros.
  const seed = {
    protein_g: Number(baseline?.protein_g ?? 0),
    carbs_g:   Number(baseline?.carbs_g   ?? 0),
    fiber_g:   Number(baseline?.fiber_g   ?? 0),
    fat_g:     Number(baseline?.fat_g     ?? 0),
  };
  const [macros, setMacros] = useState(seed);
  const [result, setResult] = useState({
    nis_pct: Number(baseline?.nis_pct ?? 0),
    dil_g:   Number(baseline?.dil_g   ?? 0),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debounceRef = useRef(null);

  const baselineNis = Number(baseline?.nis_pct ?? 0);
  const baselineDil = Number(baseline?.dil_g   ?? 0);

  // Debounced fetch — only fire if macros differ from seed.
  useEffect(() => {
    const isBaseline = SLIDERS.every(
      (s) => Math.abs((macros[s.key] || 0) - (seed[s.key] || 0)) < 0.05
    );
    if (isBaseline) {
      setResult({ nis_pct: baselineNis, dil_g: baselineDil });
      setError(null);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.post(
          `${apiBase}/bhi/nis-dil-whatif`,
          {
            protein_g: macros.protein_g,
            carbs_g:   macros.carbs_g,
            fiber_g:   macros.fiber_g,
            fat_g:     macros.fat_g,
          },
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        );
        setResult({
          nis_pct: Number(res.data?.nis_pct ?? 0),
          dil_g:   Number(res.data?.dil_g   ?? 0),
        });
      } catch (e) {
        setError("Неуспешно изчисление");
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [macros]);

  const deltaNis = useMemo(() => result.nis_pct - baselineNis, [result, baselineNis]);
  const deltaDil = useMemo(() => result.dil_g   - baselineDil, [result, baselineDil]);

  const reset = () => setMacros(seed);

  const applySwap = (swap) => {
    setMacros((prev) => ({
      protein_g: CLAMP((prev.protein_g || 0) + swap.delta.protein_g, 0, 200),
      carbs_g:   CLAMP((prev.carbs_g   || 0) + swap.delta.carbs_g,   0, 300),
      fiber_g:   CLAMP((prev.fiber_g   || 0) + swap.delta.fiber_g,   0, 50),
      fat_g:     CLAMP((prev.fat_g     || 0) + swap.delta.fat_g,     0, 150),
    }));
  };

  const bg = isDark ? "bg-slate-900" : "bg-white";
  const border = isDark ? "border-white/10" : "border-slate-200";
  const text = isDark ? "text-slate-200" : "text-slate-800";
  const muted = isDark ? "text-slate-400" : "text-slate-500";

  const deltaColor = (d, inverse = false) => {
    if (Math.abs(d) < 0.1) return muted;
    const good = inverse ? d > 0 : d < 0;
    return good ? "text-emerald-500" : "text-rose-500";
  };
  const deltaIcon = (d) =>
    Math.abs(d) < 0.1 ? null : d < 0 ? (
      <TrendingDown className="w-3 h-3 inline" />
    ) : (
      <TrendingUp className="w-3 h-3 inline" />
    );

  return (
    <div
      data-testid="whatif-nis-dil-calculator"
      className={`${bg} ${border} border rounded-xl p-4 shadow-xl w-[340px] text-xs ${text}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-sm flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5" />
          Какво ако…?
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="whatif-reset-btn"
            onClick={reset}
            className={`p-1 rounded hover:bg-slate-500/10 ${muted}`}
            title="Възстанови оригинала"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            data-testid="whatif-close-btn"
            onClick={onClose}
            className={`p-1 rounded hover:bg-slate-500/10 ${muted}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {baseline?.name_bg && (
        <div className={`text-[10px] ${muted} mb-3 line-clamp-2`}>
          Базово ястие: {baseline.name_bg}
        </div>
      )}

      {/* iter868 — Preset swap chips */}
      <div className={`text-[9.5px] ${muted} mb-1 font-semibold`}>
        Бързи замени:
      </div>
      <div className="flex flex-wrap gap-1 mb-3" data-testid="whatif-preset-swaps">
        {PRESET_SWAPS.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => applySwap(s)}
            title={s.tooltip}
            data-testid={`whatif-swap-${s.id}`}
            className={`px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold transition ${
              isDark
                ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Sliders */}
      <div className="space-y-2.5 mb-3">
        {SLIDERS.map((s) => (
          <div key={s.key}>
            <div className="flex justify-between mb-0.5">
              <label className="font-medium">{s.label}</label>
              <span className={muted}>
                {Number(macros[s.key] || 0).toFixed(s.step < 1 ? 1 : 0)} {s.unit}
              </span>
            </div>
            <input
              data-testid={`whatif-slider-${s.key}`}
              type="range"
              min={s.min}
              max={s.max}
              step={s.step}
              value={macros[s.key] || 0}
              onChange={(e) =>
                setMacros((prev) => ({
                  ...prev,
                  [s.key]: CLAMP(parseFloat(e.target.value), s.min, s.max),
                }))
              }
              className="w-full accent-emerald-500 h-1"
            />
          </div>
        ))}
      </div>

      {/* Result */}
      <div className={`rounded-lg p-2.5 ${isDark ? "bg-white/[0.04]" : "bg-slate-50"} space-y-1.5`}>
        <div className="flex justify-between items-baseline">
          <span className={muted}>NIS (%)</span>
          <div className="flex items-baseline gap-2">
            <span className={`${muted} line-through text-[10px]`}>
              {baselineNis.toFixed(1)}
            </span>
            <span
              className="font-bold text-sm"
              data-testid="whatif-result-nis"
            >
              {result.nis_pct.toFixed(1)}
            </span>
            <span className={`${deltaColor(deltaNis)} text-[10px] font-semibold`}>
              {deltaIcon(deltaNis)}{" "}
              {deltaNis > 0 ? "+" : ""}
              {deltaNis.toFixed(1)}
            </span>
          </div>
        </div>
        <div className="flex justify-between items-baseline">
          <span className={muted}>DIL (g)</span>
          <div className="flex items-baseline gap-2">
            <span className={`${muted} line-through text-[10px]`}>
              {baselineDil.toFixed(1)}
            </span>
            <span
              className="font-bold text-sm"
              data-testid="whatif-result-dil"
            >
              {result.dil_g.toFixed(1)}
            </span>
            <span className={`${deltaColor(deltaDil)} text-[10px] font-semibold`}>
              {deltaIcon(deltaDil)}{" "}
              {deltaDil > 0 ? "+" : ""}
              {deltaDil.toFixed(1)}
            </span>
          </div>
        </div>
        {loading && (
          <div className={`text-[10px] ${muted} italic pt-0.5`}>изчислява…</div>
        )}
        {error && (
          <div className="text-[10px] text-rose-500 pt-0.5">{error}</div>
        )}
      </div>

      <div className={`text-[9px] ${muted} mt-2 leading-tight`}>
        NIS = Nutramilo Insulin Score (v1.1.9). DIL = Dietary Insulin Load
        (0.69·протеин + max(0, въгл.−фибри)). Изчислено от каноничните библиотеки.
      </div>
    </div>
  );
};

export default WhatIfNisDilCalculator;
